// ============================================================
// ActorStatusView - the acting character's Status view
// ============================================================
// Built ONCE by AttackerPanel and rendered wherever is live: the Status
// tab when it is selected, the hover curtain's glass when the cursor is
// elsewhere. Its condition and health edits write the actor's combat
// state through `updateCombatantState`.

import CombatantStatusTab from './CombatantStatusTab';
import { TurnStrip } from './StatusBand';
import WoundsReadout from './WoundsReadout';
import ConditionsSection from './ConditionsSection';
import { FrenzyChip } from './FrenzyChip';
import ExposuresSection, { ExposeButton } from './ExposuresSection';
import BodyMap, { ConditionRow } from './BodyMap.jsx';
import AttackerBodyMap, { AttackerTraitRow, AttackerStatusChips } from './AttackerBodyMap';
import { isExposureCode } from '../engine/exposures';
import { mergeCondition } from '../engine/conditions';
import { teamsIn } from '../engine/roster/team';

/**
 * The actor's condition and health writes.
 * @param {string} actorId
 * @param {function} updateCombatantState `(id, (state) => state)`
 * @param {string} stackPolicy the session's; a picker may name its own
 */
export function actorEdits(actorId, updateCombatantState, stackPolicy) {
  const update = (fn) => updateCombatantState(actorId, fn);
  const filterConditions = (keep) => update((st) => ({ ...st, conditions: st.conditions.filter(keep) }));
  return {
    remove: (index) => filterConditions((_, i) => i !== index),
    add: (code, opts) =>
      update((st) => {
        const next = [...st.conditions];
        mergeCondition(next, code, opts?.stacking ?? stackPolicy);
        return { ...st, conditions: next };
      }),
    clearConditions: () => filterConditions(isExposureCode),
    clearExposures: () => filterConditions((c) => !isExposureCode(c)),
    /** A horde's magnitude, anyone else's current wounds. */
    setHealth: (isHorde, value) =>
      update((st) => ({ ...st, ...(isHorde ? { hordeMagnitude: value } : { woundsCurrent: value }) })),
  };
}

/**
 * @param {object} props `{ actor, actorTarget, edits, locked, detailed, roster, turnTrackerState,
 *   rerollInitiative, sourceCharacter, isMelee, onEditArmour, chips }` - `chips` are the
 *   AttackerStatusChips props.
 */
export default function ActorStatusView(props) {
  const { actor, actorTarget, edits, locked, sourceCharacter } = props;
  const reroll = props.rerollInitiative;
  return (
    <CombatantStatusTab
      turnStrip={
        <TurnStrip
          tracker={props.turnTrackerState}
          combatantId={actor.id}
          onRerollInitiative={reroll ? () => reroll(`tab-${actor.id}`) : null}
        />
      }
      bodyMap={<ActorMap {...props} />}
      traitsRow={<AttackerTraitRow sourceCharacter={sourceCharacter} isMelee={props.isMelee} target={actorTarget} />}
      conditionsRow={<ConditionRow target={actorTarget} onRemoveCondition={locked ? undefined : edits.remove} />}
      wounds={
        <WoundsReadout
          target={actorTarget}
          locked={locked}
          onChange={(value) => edits.setHealth(!!actorTarget.horde?.isHorde, value)}
        />
      }
      footer={<ActorStatusFooter {...props} />}
    />
  );
}

/**
 * v7.15.0 - by default the SAME interactive map the target panel uses:
 * your own armour, wounds, cover, crippled limbs and fire are as worth
 * seeing as an enemy's. A GM who would rather not have their players' own
 * armour numbers on screen turns the house rule off and gets the plain
 * silhouette. One gesture grammar app-wide (v8.243.0): armour edits are
 * a RIGHT-click; the actor's own map never gets `targeting` - no sights
 * on yourself.
 */
function ActorMap({ detailed, actor, actorTarget, sourceCharacter, locked, onEditArmour, impact }) {
  if (!detailed) {
    return (
      <AttackerBodyMap
        key={actor.id}
        variant="fill"
        sourceCharacter={sourceCharacter}
        actorTarget={actorTarget}
        figureSheet={actor.sheet}
        impact={impact}
      />
    );
  }
  return (
    <BodyMap
      // One plate per combatant: a switch of actor is a new plate, its live impact history (never replayed).
      key={actor.id}
      variant="fill"
      side="actor"
      target={actorTarget}
      figureSheet={actor.sheet}
      sourceCharacter={sourceCharacter}
      onLocationContextMenu={locked ? undefined : onEditArmour}
      impact={impact}
      combatantId={actor.id}
    />
  );
}

/** The status chips, the conditions, and what others see of the actor - the exposures (v8.351.0). */
function ActorStatusFooter({ actorTarget, edits, locked, roster, chips, onToggleFrenzied }) {
  return (
    <>
      <AttackerStatusChips {...chips} />
      <ConditionsSection
        target={actorTarget}
        locked={locked}
        onAdd={edits.add}
        onRemove={edits.remove}
        onClear={edits.clearConditions}
        extraActions={
          <>
            <FrenzyChip frenzied={!!actorTarget.frenzied} onToggle={onToggleFrenzied} locked={locked} />
            <ExposeButton onAdd={edits.add} locked={locked} teams={teamsIn(roster)} />
          </>
        }
      />
      <ExposuresSection target={actorTarget} locked={locked} onRemove={edits.remove} onClear={edits.clearExposures} />
    </>
  );
}
